import {
  combineReducers,
  configureStore,
} from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

// Import slices and APIs
import { baseApi } from "./api/api"; // Base API extended by other APIs
import { authApi } from "./api/authApi"; // Authentication API
import utilsReducer from "./slices/utilsSlice";
import sessionReducer from "./slices/sessionSlice";
import categoryReducer from "./slices/categoryListSlice";
import courseReducer from "./slices/courseSlice";
import exerciseReducer from "./slices/exerciseListSlice";
import answersReducer from "./slices/answersSlice";
import toastReducer from "./slices/toastSlice";
import deferredActionsReducer from "./slices/deferredActionsSlice";
import impersonationReducer from "./slices/impersonationSlice";

import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";

// Combine reducers, including slices and RTK Query reducers
const rootReducer = combineReducers({
  utils: utilsReducer,
  session: sessionReducer,
  categoryList: categoryReducer,
  courseList: courseReducer,
  exerciseList: exerciseReducer,
  answers: answersReducer,
  toasts: toastReducer,
  deferredActions: deferredActionsReducer,
  impersonation: impersonationReducer,
  [baseApi.reducerPath]: baseApi.reducer, // Base API reducer
  [authApi.reducerPath]: authApi.reducer, // Auth API reducer
});

// Configure the store with reducers and middleware for RTK Query
const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(baseApi.middleware) // Add baseApi middleware
      .concat(authApi.middleware), // Add authApi middleware
  devTools: process.env.NODE_ENV !== "production",
});

setupListeners(store.dispatch);

// Export RootState type based on the store’s reducer structure
export type RootState = ReturnType<typeof rootReducer>;

// Export AppDispatch type to use in components for dispatching actions
export type AppDispatch = typeof store.dispatch;

// Custom hooks for typed dispatch and selector
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export default store;
